import { useCallback, useMemo, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useApplicationsApplyMutation } from '@/api/methods/useApplicationsApplyMutation.ts';
import { useApplicationIndustries } from '@/api/methods/useApplicationIndustries.ts';
import { useApplicationContext } from '@/contexts/ApplicationContext';
import { trackIndustrySelected } from '@/lib/trackIndustrySelected.ts';
import { trackPositionSelected } from '@/lib/trackPositionSelected.ts';
import { trackFormStart } from '@/lib/trackFormStart.ts';
import { trackApplicationSubmitted } from '@/lib/trackApplicationSubmitted.ts';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { PHONE_NUMBER_REGEX, ZIP_CODE_REGEX } from '@/constants/validations.ts';
import { showFormError } from '@/lib/showFormError.ts';
import { useIndustryNameGetter } from '@/hooks/useIndustryNameGetter.ts';
import { usePositionNameGetter } from '@/hooks/usePositionNameGetter.ts';

type FormValues = {
  client_id: string;
  position_id: string;
  first_name: string;
  last_name: string;
  phone: string;
  zip_code: string;
};

export const ApplyForm = () => {
  const { t } = useTranslation();
  const { data: industries } = useApplicationIndustries();
  const { mutateAsync: applicationsApply } = useApplicationsApplyMutation();
  const { industryIdToApply, setIndustryIdToApply } = useApplicationContext();
  const [params] = useSearchParams();
  const industryFromParams = params.get('industry') as string | null;
  const { i18n } = useTranslation();
  const { language } = i18n;
  const hasTrackedFormStart = useRef(false);
  const navigate = useNavigate();
  const getIndustryName = useIndustryNameGetter();
  const getPositionName = usePositionNameGetter();

  const formSchema = z.object({
    client_id: z.string().min(1, t('form.errors.industryRequired')),
    position_id: z.string().min(1, t('form.errors.jobRequired')),
    first_name: z.string().min(1, t('form.errors.firstNameRequired')),
    last_name: z.string().min(1, t('form.errors.lastNameRequired')),
    phone: z
      .string()
      .min(1, t('form.errors.phoneRequired'))
      .regex(PHONE_NUMBER_REGEX, { message: t('form.errors.phoneInvalid') }),
    zip_code: z
      .string()
      .min(1, t('form.errors.zipCodeRequired'))
      .regex(ZIP_CODE_REGEX, { message: t('form.errors.zipCodeInvalid') }),
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      client_id: '',
      position_id: '',
      first_name: '',
      last_name: '',
      phone: '',
      zip_code: '',
    },
  });

  const selectedIndustryId = form.watch('client_id');

  // Track form start on first meaningful interaction
  const handleFormStart = () => {
    if (!hasTrackedFormStart.current) {
      hasTrackedFormStart.current = true;
      trackFormStart();
    }
  };

  const selectIndustry = useCallback(
    (
      industryId: string | null,
      { shouldTrackEvent }: { shouldTrackEvent: boolean }
    ) => {
      if (!industryId || !industries?.length) {
        return;
      }

      const selectedIndustry = industries.find(
        (industry) => industry.client_id?.toString() === industryId
      );
      if (!selectedIndustry) {
        return;
      }

      // Setting new value
      form.setValue('client_id', industryId);
      // Clear context after setting to avoid conflicts
      setIndustryIdToApply(null);
      // Clear position when industry changes
      form.setValue('position_id', '');

      // Track industry selection
      if (shouldTrackEvent && selectedIndustry?.display_name) {
        trackIndustrySelected(selectedIndustry.display_name);
      }
    },
    [industries, form, setIndustryIdToApply]
  );

  // Set industry from context when it changes
  useEffect(() => {
    selectIndustry(industryIdToApply, { shouldTrackEvent: true });
  }, [selectIndustry, industryIdToApply]);

  // Set industry from query params when it changes
  useEffect(() => {
    selectIndustry(industryFromParams, { shouldTrackEvent: false });
  }, [selectIndustry, industryFromParams]);

  const positionsBySelectedIndustry = useMemo(() => {
    if (!selectedIndustryId || !industries?.length) {
      return [];
    }
    const selectedIndustry = industries.find(
      (industry) => industry.client_id?.toString() === selectedIndustryId
    );
    return selectedIndustry?.positions ?? [];
  }, [industries, selectedIndustryId]);

  const onSubmit = async (data: FormValues) => {
    try {
      const { interview_link } = await applicationsApply({
        ...data,
        position_id: Number(data.position_id),
        language: getFullLanguageName(language),
      });

      const selectedIndustry = industries?.find(
        (industry) => industry.client_id?.toString() === data.client_id
      );
      const selectedPosition = selectedIndustry?.positions?.find(
        (position) => position.id?.toString() === data.position_id
      );
      const hasLocation = Boolean(data.zip_code?.trim());
      trackApplicationSubmitted({
        industry: selectedIndustry?.display_name ?? '',
        position: selectedPosition?.name ?? '',
        hasLocation,
      });

      navigate('/finish', {
        replace: true,
        state: { interviewLink: interview_link },
      });
    } catch (error) {
      showFormError({ hookForm: form, error });
    }
  };

  return (
    <div
      id="apply-form"
      className="w-full bg-[#DDE5E3] px-6 md:px-8 pt-10 pb-16"
    >
      {/* Title */}
      <h2 className="text-2xl md:text-3xl font-bold text-center mb-6">
        {t('form.title')}
      </h2>

      {/* Form */}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Industry */}
          <FormField
            control={form.control}
            name="client_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>{t('form.industry.label')}</FormLabel>
                <Select
                  onOpenChange={handleFormStart}
                  onValueChange={(value) =>
                    selectIndustry(value, { shouldTrackEvent: true })
                  }
                  value={field.value}
                >
                  <FormControl>
                    <SelectTrigger onFocus={handleFormStart}>
                      <SelectValue
                        placeholder={t('form.industry.placeholder')}
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {industries?.map((industry, index) => (
                      <SelectItem
                        key={index}
                        value={industry.client_id?.toString() ?? ''}
                      >
                        {getIndustryName(industry)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Job */}
          <FormField
            control={form.control}
            name="position_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>{t('form.position.label')}</FormLabel>
                <Select
                  onOpenChange={handleFormStart}
                  onValueChange={(value) => {
                    field.onChange(value);
                    // Track position selection
                    const selectedPosition = positionsBySelectedIndustry.find(
                      (position) => position.id?.toString() === value
                    );
                    if (selectedPosition?.name) {
                      trackPositionSelected(selectedPosition.name);
                    }
                  }}
                  value={field.value}
                  disabled={!positionsBySelectedIndustry.length}
                >
                  <FormControl>
                    <SelectTrigger onFocus={handleFormStart}>
                      <SelectValue
                        placeholder={t('form.position.placeholder')}
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {positionsBySelectedIndustry.map((position, index) => (
                      <SelectItem
                        key={index}
                        value={position.id?.toString() ?? ''}
                      >
                        {getPositionName(position)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Full Name */}
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="first_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>{t('form.firstName.label')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      autoComplete="given-name"
                      placeholder={t('form.firstName.placeholder')}
                      onFocus={handleFormStart}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="last_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>{t('form.lastName.label')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      autoComplete="family-name"
                      placeholder={t('form.lastName.placeholder')}
                      onFocus={handleFormStart}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Phone */}
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>{t('form.phone.label')}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    kind="phone_number"
                    autoComplete="tel-national"
                    placeholder={t('form.phone.placeholder')}
                    onFocus={handleFormStart}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Zip Code */}
          <FormField
            control={form.control}
            name="zip_code"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>{t('form.zipCode.label')}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    kind="zipcode"
                    autoComplete="postal-code"
                    placeholder={t('form.zipCode.placeholder')}
                    onFocus={handleFormStart}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Submit Button */}
          <Button
            type="submit"
            variant="accent"
            size="lg"
            className="w-full uppercase font-bold"
          >
            {t('form.submit')}
          </Button>
        </form>
      </Form>
    </div>
  );
};

function getFullLanguageName(
  langCode: string
): 'english' | 'spanish' | 'russian' {
  switch (langCode) {
    case 'en':
      return 'english';
    case 'es':
      return 'spanish';
    case 'ru':
      return 'russian';
    default:
      return 'english';
  }
}
